/**
 * LOGIN EVENT TRACKER UTILITY
 * 
 * ⚠️  CRITICAL: DO NOT MODIFY THIS FILE WITHOUT UNDERSTANDING THE IMPLICATIONS ⚠️
 * 
 * This utility prevents duplicate login audit log entries from being created when:
 * - User refreshes the page
 * - Firebase auth state is restored
 * - Component re-mounts
 * 
 * PROBLEM: Firebase's onAuthStateChanged triggers on every page load/refresh when a user
 * is already authenticated, causing duplicate "Login success" audit logs.
 * 
 * SOLUTION: Track login events per browser session using sessionStorage, which:
 * - Persists across page refreshes within the same tab
 * - Gets cleared when the browser tab is closed
 * - Allows new login events when user actually logs in again
 * 
 * RULES:
 * 1. NEVER remove the sessionStorage tracking
 * 2. NEVER change the session key format without updating all references
 * 3. ALWAYS use this utility instead of direct sessionStorage access
 * 4. ALWAYS clear tracking on logout
 */

// Session storage key prefix - DO NOT CHANGE without updating all references
const LOGIN_EVENT_PREFIX = 'plutoo_login_logged_';
const BACKUP_PREFIX = 'plutoo_login_backup_';

/**
 * Check if a login event has already been logged for this user in this session
 * 
 * @param {string} userId - The user's UID
 * @returns {boolean} - True if login event already logged, false otherwise
 */
export const hasLoggedLoginEvent = (userId) => {
  if (!userId) {
    console.warn('LoginEventTracker: No userId provided to hasLoggedLoginEvent');
    return false;
  }
  
  try {
    const sessionKey = `${LOGIN_EVENT_PREFIX}${userId}`;
    const backupKey = `${BACKUP_PREFIX}${userId}`;
    
    // Check both primary and backup keys for extra safety
    const primaryCheck = sessionStorage.getItem(sessionKey);
    const backupCheck = sessionStorage.getItem(backupKey);
    
    const hasLogged = !!(primaryCheck || backupCheck);
    
    if (hasLogged) {
      console.log(`⏭️ LoginEventTracker: Login already logged for user ${userId} in this session`);
    }
    
    return hasLogged;
  } catch (error) {
    console.error('LoginEventTracker: Error checking login event status:', error);
    // If sessionStorage fails, assume not logged to avoid blocking legitimate logins
    return false;
  }
};

/**
 * Mark that a login event has been logged for this user in this session
 * 
 * @param {string} userId - The user's UID
 * @param {string} timestamp - Optional timestamp of the login event
 */
export const markLoginEventLogged = (userId, timestamp = null) => {
  if (!userId) {
    console.warn('LoginEventTracker: No userId provided to markLoginEventLogged');
    return;
  }
  
  try {
    const sessionKey = `${LOGIN_EVENT_PREFIX}${userId}`;
    const backupKey = `${BACKUP_PREFIX}${userId}`;
    const eventData = {
      logged: true,
      timestamp: timestamp || new Date().toISOString(),
      userAgent: navigator.userAgent.substring(0, 100) // Truncated for storage efficiency
    };
    
    const eventDataString = JSON.stringify(eventData);
    
    // Store in both primary and backup locations for redundancy
    sessionStorage.setItem(sessionKey, eventDataString);
    sessionStorage.setItem(backupKey, eventDataString);
    
    console.log(`✅ LoginEventTracker: Marked login event as logged for user ${userId}`);
  } catch (error) {
    console.error('LoginEventTracker: Error marking login event as logged:', error);
    // Don't throw - this shouldn't break the login flow
  }
};

/**
 * Clear login event tracking for a specific user
 * 
 * @param {string} userId - The user's UID
 */
export const clearUserLoginTracking = (userId) => {
  if (!userId) {
    console.warn('LoginEventTracker: No userId provided to clearUserLoginTracking');
    return;
  }
  
  try {
    const sessionKey = `${LOGIN_EVENT_PREFIX}${userId}`;
    const backupKey = `${BACKUP_PREFIX}${userId}`;
    
    sessionStorage.removeItem(sessionKey);
    sessionStorage.removeItem(backupKey);
    
    console.log(`🧹 LoginEventTracker: Cleared login tracking for user ${userId}`);
  } catch (error) {
    console.error('LoginEventTracker: Error clearing user login tracking:', error);
  }
};

/**
 * Clear ALL login event tracking (used on logout)
 * 
 * ⚠️ CRITICAL: This must be called on logout to prevent stale tracking data
 */
export const clearAllLoginTracking = () => {
  try {
    const keysToRemove = [];
    
    // Find all login tracking keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith(LOGIN_EVENT_PREFIX) || key.startsWith(BACKUP_PREFIX))) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all found keys
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });
    
    console.log(`🧹 LoginEventTracker: Cleared ${keysToRemove.length} login tracking entries`);
  } catch (error) {
    console.error('LoginEventTracker: Error clearing all login tracking:', error);
  }
};

/**
 * Get debug information about current login tracking state
 * 
 * @returns {Object} Debug information
 */
export const getLoginTrackingDebugInfo = () => {
  try {
    const trackingData = {};
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith(LOGIN_EVENT_PREFIX) || key.startsWith(BACKUP_PREFIX))) {
        try {
          trackingData[key] = JSON.parse(sessionStorage.getItem(key));
        } catch (e) {
          trackingData[key] = sessionStorage.getItem(key);
        }
      }
    }
    
    return {
      trackingData,
      sessionStorageAvailable: typeof(Storage) !== "undefined",
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('LoginEventTracker: Error getting debug info:', error);
    return { error: error.message };
  }
};

/**
 * Validate the integrity of the login tracking system
 * Call this on app initialization to ensure the system is working
 * 
 * @returns {boolean} True if system is working correctly
 */
export const validateLoginTracker = () => {
  try {
    const testKey = 'plutoo_login_tracker_test';
    const testValue = 'test_value';
    
    // Test sessionStorage functionality
    sessionStorage.setItem(testKey, testValue);
    const retrieved = sessionStorage.getItem(testKey);
    sessionStorage.removeItem(testKey);
    
    if (retrieved !== testValue) {
      console.error('LoginEventTracker: SessionStorage test failed');
      return false;
    }
    
    console.log('✅ LoginEventTracker: System validation passed');
    return true;
  } catch (error) {
    console.error('LoginEventTracker: System validation failed:', error);
    return false;
  }
}; 